import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import EP from "../constants/api-endpoints";

type CB = { onSuccess?: (r: any) => void; onError?: (e: any) => void };

// const sel = (r: any) => r.data;
const listSel = (r: any) => ({ data: r.data.data as any[], total: r.data.extra?.total ?? 0 });
const statsSel = (r: any) => r.data.data as Record<string, number>;

// ── Shared dropdowns ──────────────────────────────────────────────────────

export const useFinClients = (cid: number) =>
  useQuery({ queryKey: [EP.CF_FIN_CLIENTS(cid)], queryFn: () => client.get(EP.CF_FIN_CLIENTS(cid)), select: (r: any) => r.data.data as any[], enabled: !!cid, refetchOnWindowFocus: false });

export const useFinSuppliers = (cid: number) =>
  useQuery({ queryKey: [EP.CF_FIN_SUPPLIERS(cid)], queryFn: () => client.get(EP.CF_FIN_SUPPLIERS(cid)), select: (r: any) => r.data.data as any[], enabled: !!cid, refetchOnWindowFocus: false });

export const useFinUsers = (cid: number) =>
  useQuery({ queryKey: [EP.CF_FIN_USERS(cid)], queryFn: () => client.get(EP.CF_FIN_USERS(cid)), select: (r: any) => r.data.data as any[], enabled: !!cid, refetchOnWindowFocus: false });

// ── Invoices ──────────────────────────────────────────────────────────────

export const useInvoiceList = (cid: number, params: any = {}) =>
  useQuery({ queryKey: [EP.CF_INVOICES(cid), params], queryFn: () => client.get(EP.CF_INVOICES(cid), { params }), select: listSel, enabled: !!cid, refetchOnWindowFocus: false });

export const useInvoiceStats = (cid: number) =>
  useQuery({ queryKey: [EP.CF_INVOICE_STATS(cid)], queryFn: () => client.get(EP.CF_INVOICE_STATS(cid)), select: statsSel, enabled: !!cid, refetchOnWindowFocus: false });

export const useInvoiceDetail = (cid: number, id: number) =>
  useQuery({ queryKey: [EP.CF_INVOICE_DETAIL(cid, id)], queryFn: () => client.get(EP.CF_INVOICE_DETAIL(cid, id)), select: (r: any) => r.data.data, enabled: !!cid && !!id, refetchOnWindowFocus: false });

const useInvMut = (cid: number, url: (c: number) => string, cb: CB = {}) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (d: any) => client.post(url(cid), d),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: [EP.CF_INVOICES(cid)] });
      qc.invalidateQueries({ queryKey: [EP.CF_INVOICE_STATS(cid)] });
      cb.onSuccess?.(r);
    },
    onError: cb.onError,
  });
};

export const useInvoiceCreate  = (cid: number, cb?: CB) => useInvMut(cid, EP.CF_INVOICE_CREATE, cb);
export const useInvoiceUpdate  = (cid: number, cb?: CB) => useInvMut(cid, EP.CF_INVOICE_UPDATE, cb);
export const useInvoiceDelete  = (cid: number, cb?: CB) => useInvMut(cid, EP.CF_INVOICE_DELETE, cb);
export const useInvoiceStatus  = (cid: number, cb?: CB) => useInvMut(cid, EP.CF_INVOICE_STATUS, cb);
export const useInvoicePayment = (cid: number, cb?: CB) => useInvMut(cid, EP.CF_INVOICE_PAYMENT, cb);

// ── Quotes ────────────────────────────────────────────────────────────────

export const useQuoteList = (cid: number, params: any = {}) =>
  useQuery({ queryKey: [EP.CF_QUOTES(cid), params], queryFn: () => client.get(EP.CF_QUOTES(cid), { params }), select: listSel, enabled: !!cid, refetchOnWindowFocus: false });

export const useQuoteStats = (cid: number) =>
  useQuery({ queryKey: [EP.CF_QUOTE_STATS(cid)], queryFn: () => client.get(EP.CF_QUOTE_STATS(cid)), select: statsSel, enabled: !!cid, refetchOnWindowFocus: false });

export const useQuoteDetail = (cid: number, id: number) =>
  useQuery({ queryKey: [EP.CF_QUOTE_DETAIL(cid, id)], queryFn: () => client.get(EP.CF_QUOTE_DETAIL(cid, id)), select: (r: any) => r.data.data, enabled: !!cid && !!id, refetchOnWindowFocus: false });

const useQMut = (cid: number, url: (c: number) => string, cb: CB = {}) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (d: any) => client.post(url(cid), d),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: [EP.CF_QUOTES(cid)] });
      qc.invalidateQueries({ queryKey: [EP.CF_QUOTE_STATS(cid)] });
      cb.onSuccess?.(r);
    },
    onError: cb.onError,
  });
};

export const useQuoteCreate = (cid: number, cb?: CB) => useQMut(cid, EP.CF_QUOTE_CREATE, cb);
export const useQuoteUpdate = (cid: number, cb?: CB) => useQMut(cid, EP.CF_QUOTE_UPDATE, cb);
export const useQuoteDelete = (cid: number, cb?: CB) => useQMut(cid, EP.CF_QUOTE_DELETE, cb);
export const useQuoteSend   = (cid: number, cb?: CB) => useQMut(cid, EP.CF_QUOTE_SEND, cb);

// ── Purchase Orders ───────────────────────────────────────────────────────

export const usePOList = (cid: number, params: any = {}) =>
  useQuery({ queryKey: [EP.CF_POS(cid), params], queryFn: () => client.get(EP.CF_POS(cid), { params }), select: listSel, enabled: !!cid, refetchOnWindowFocus: false });

export const usePOStats = (cid: number) =>
  useQuery({ queryKey: [EP.CF_PO_STATS(cid)], queryFn: () => client.get(EP.CF_PO_STATS(cid)), select: statsSel, enabled: !!cid, refetchOnWindowFocus: false });

export const usePODetail = (cid: number, id: number) =>
  useQuery({ queryKey: [EP.CF_PO_DETAIL(cid, id)], queryFn: () => client.get(EP.CF_PO_DETAIL(cid, id)), select: (r: any) => r.data.data, enabled: !!cid && !!id, refetchOnWindowFocus: false });

const usePOMut = (cid: number, url: (c: number) => string, cb: CB = {}) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (d: any) => client.post(url(cid), d),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: [EP.CF_POS(cid)] });
      qc.invalidateQueries({ queryKey: [EP.CF_PO_STATS(cid)] });
      cb.onSuccess?.(r);
    },
    onError: cb.onError,
  });
};

export const usePOCreate = (cid: number, cb?: CB) => usePOMut(cid, EP.CF_PO_CREATE, cb);
export const usePOUpdate = (cid: number, cb?: CB) => usePOMut(cid, EP.CF_PO_UPDATE, cb);
export const usePODelete = (cid: number, cb?: CB) => usePOMut(cid, EP.CF_PO_DELETE, cb);
export const usePOStatus = (cid: number, cb?: CB) => usePOMut(cid, EP.CF_PO_STATUS, cb);
export const usePOCancel = (cid: number, cb?: CB) => usePOMut(cid, EP.CF_PO_CANCEL, cb);

// All POs dropdown (for invoice from-PO selector)
export const useAllPOs = (cid: number) =>
  useQuery({
    queryKey: [EP.CF_POS(cid), "all"],
    queryFn: () => client.get(EP.CF_POS(cid), { params: { limit: 200 } }),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });

// All Quotes dropdown (for PO from-quote selector)
export const useAllQuotes = (cid: number) =>
  useQuery({
    queryKey: [EP.CF_QUOTES(cid), "all"],
    queryFn: () => client.get(EP.CF_QUOTES(cid), { params: { limit: 200 } }),
    select: (r: any) => r.data.data as any[],
    enabled: !!cid,
    refetchOnWindowFocus: false,
  });
